import cn from 'classnames'
import { useCallback } from 'react'

import styles from './SegmentControl.module.scss'

export interface SegmentControlItem<TValue extends string> {
  value: TValue
  label: string
  isDisabled?: boolean
}

interface SegmentControlProps<TValue extends string> {
  className?: string
  items: SegmentControlItem<TValue>[]
  value: TValue
  onChange: (value: TValue) => void
}

export const SegmentControl = <TValue extends string>({
  className,
  items,
  value,
  onChange,
}: SegmentControlProps<TValue>) => {
  return (
    <div className={cn(styles.segmentControl, className)}>
      {items.map((item) => (
        <SegmentControlOption
          key={item.value}
          item={item}
          isActive={item.value === value && !item.isDisabled}
          onChange={onChange}
        />
      ))}
    </div>
  )
}

interface SegmentControlOptionProps<TValue extends string> {
  item: SegmentControlItem<TValue>
  isActive: boolean
  onChange: (value: TValue) => void
}

const SegmentControlOption = <TValue extends string>({
  item,
  isActive,
  onChange,
}: SegmentControlOptionProps<TValue>) => {
  const selectOption = useCallback(() => {
    onChange(item.value)
  }, [item.value, onChange])

  return (
    <button
      type="button"
      className={cn(styles.item, {
        [styles.active]: isActive,
        [styles.disabled]: item.isDisabled,
      })}
      disabled={item.isDisabled}
      onClick={selectOption}
    >
      {item.label}
    </button>
  )
}
